import { memo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TmdxSelect, TmdxTextarea } from '@ds';
import { AdminFormRow } from '@ui';
import { renderSelectItem } from '@/utils/helpers';
import { MAX_LENGTH_2000 } from '@/constants/form.constants';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { useTrialProgramsList } from '@/hooks/useTrialProgramsList';
import { NewTXCenterForm } from '../types';
import { EUROPE_REGION_ID } from '@/constants/serviceRegion.constants';

type TXCenterProgramsProps = {
  isReadOnly?: boolean;
  isNotesReadOnly?: boolean;
};

export const TXCenterPrograms = memo(({ isReadOnly, isNotesReadOnly }: TXCenterProgramsProps) => {
  const { control } = useFormContext<NewTXCenterForm>();

  const country = useWatch({ control, name: 'country' });
  const trialProgramIds = useWatch({ control, name: 'trialProgramIds' });

  const { countriesByIso2 } = useCountriesData();
  const isEuropeanAddress = countriesByIso2[country]?.serviceRegionId === EUROPE_REGION_ID;

  const { trialProgramsList } = useTrialProgramsList();

  return (
    <>
      {!isEuropeanAddress && (
        <AdminFormRow title="Trial Programs">
          <Controller
            name="trialProgramIds"
            control={control}
            render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <TmdxSelect
                placeholder="Choose an option"
                aria-label="Trial Programs"
                selectionMode="multiple"
                selectedKeys={value || []}
                isInvalid={invalid}
                isDisabled={isReadOnly}
                errorMessage={error?.message}
                items={trialProgramsList}
                onSelectionChange={(keys) => onChange(Array.from(keys))}
                {...field}
                renderValue={(items) => {
                  const list = items.map((i) => i.data?.name).join('; ');
                  return <span className="text-m-r text-base-5">{list}</span>;
                }}
              >
                {renderSelectItem}
              </TmdxSelect>
            )}
          />
        </AdminFormRow>
      )}
      {!!trialProgramIds?.length && (
        <AdminFormRow title="Trial Notes" center={false}>
          <Controller
            name="trialProgramNotes"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <TmdxTextarea
                fullWidth
                disableAnimation
                minRows={2}
                aria-label="Trial Notes"
                isInvalid={invalid}
                errorMessage={error?.message}
                isDisabled={isNotesReadOnly}
                className="min-h-16"
                value={value || ''}
                maxLength={MAX_LENGTH_2000}
                {...field}
              />
            )}
          />
        </AdminFormRow>
      )}
    </>
  );
});
